import {useMemo} from 'react';

import {
  type SemanticLegendByMetric,
  useSemanticLegendsQueryWithCategoricalConfigQuery,
} from '../../../../../generated/graphql';
import {NetworkStatus} from '../../../../../state/graphql/apolloWithAbort';
import {type YAxisValues} from '../../types';
import {constructSemanticLegendDataWithCategoricalConfig} from './constructSemanticLegendData';
import {useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig} from './useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig';

export function useSemanticLegendSettingsWithCategoricalConfig(
  viewId?: string,
  _configKeys?: string[],
  localKey?: string,
  localYAxisValue?: YAxisValues
) {
  const {
    data: semanticLegendSettings,
    error: semanticLegendSettingsError,
    networkStatus,
  } = useSemanticLegendsQueryWithCategoricalConfigQuery({
    variables: {id: viewId ?? ''},
    skip: viewId === '',
    notifyOnNetworkStatusChange: true,
  });
  const semanticLegendSettingsLoading = networkStatus !== NetworkStatus.ready;

  // Get the aggregates for the current key whether it's local or actually the one saved on the server
  const currentKey =
    localKey ?? semanticLegendSettings?.view?.semanticLegend?.currentMetric; // TODO: change to currentKey

  const semanticLegendsByMetric = useMemo(
    () =>
      semanticLegendSettings?.view?.semanticLegend
        ?.semanticLegendsByMetric as SemanticLegendByMetric[],
    [semanticLegendSettings]
  );
  const getSemanticLegendPalette =
    useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig();
  const decodedSemanticLegendData = useMemo(
    () =>
      constructSemanticLegendDataWithCategoricalConfig(
        getSemanticLegendPalette,
        currentKey,
        semanticLegendsByMetric
      ),
    [currentKey, semanticLegendsByMetric, getSemanticLegendPalette]
  );

  const yAxisValue = localYAxisValue ?? decodedSemanticLegendData.yAxisValue;

  return useMemo(
    () => ({
      constructedSemanticLegendData: decodedSemanticLegendData,
      currentKey,
      semanticLegendSettingsError,
      semanticLegendSettingsLoading,
      yAxisValue,
    }),
    [
      currentKey,
      decodedSemanticLegendData,
      semanticLegendSettingsError,
      semanticLegendSettingsLoading,
      yAxisValue,
    ]
  );
}
